import { __ } from "@wordpress/i18n";
import apiFetch from '@wordpress/api-fetch';
import React, { useEffect, useState } from 'react';
import DataTable from 'react-data-table-component';
import withForm from '../../pages/withForm';
const Passkeys = () => {
    const [passkeys, setPasskeys] = useState([]);
    const [filters, setFilters] = useState({
        user_name: '',
        status: '',
        authenticator: '',
        created_date: null,
        last_used_date: null,
    });

    // Get active logins data
    useEffect(() => {
        const getAllPasskeys = async () => {
            try {
                const response = await apiFetch({
                    path: '/ultimate-security/v1/get-all-passkeys',
                    method: 'GET'
                });
    
                setPasskeys(response);
            } catch (error) {
                console.error(error);
            }
        };
    
        getAllPasskeys();
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };
    const handleToggleStatus = async (row) => {
        const newStatus = row.is_active === 1 ? 0 : 1;
        try {
            await apiFetch({
                path: '/ultimate-security/v1/toggle-passkey',
                method: 'POST',
                data: {
                    credential_id: row.credential_id,
                    status: newStatus,
                },
            });
    
            // Optimistically update the UI
            setPasskeys(prev => ({
                ...prev,
                data: prev.data.map(p =>
                    p.credential_id === row.credential_id ? { ...p, is_active: newStatus } : p
                ),
            }));
        } catch (error) {
            console.error('Status toggle failed:', error);
        }
    };
    
    
    const handleDelete = async (row) => {
        const confirmed = window.confirm(__('Are you sure you want to delete this passkey?', 'ultimate-security'));
        if (!confirmed) return;
    
        try {
            const response = await apiFetch({
                path: '/ultimate-security/v1/revoke',
                method: 'POST',
                data: { fingerprint: row.credential_id }, // ✅ make sure credential_id is correct
            });
    
            if (response.status === 'success') {
                setPasskeys(prev => ({
                    ...prev,
                    data: prev.data.filter(p => p.credential_id !== row.credential_id),
                }));
            } else {
                // console.error('Unexpected response:', response);
                alert(__('Failed to revoke passkey.', 'ultimate-security'));
            }
        } catch (error) {
            // console.error('Revoke failed:', error);
            alert(__('Error revoking passkey: ', 'ultimate-security') + error.message);
        }
    };

    const columns = [
        {
            id: 'user',
            name: __('User', 'ultimate-security'),
            cell: row => (
                <div className="d-flex flex-wrap align-items-center gap-2">
                    <span><b>{row.user_email}</b></span>
                </div>
            ),
        },
        {
            id: 'security-key-name',
            name: __('Security Key Name', 'ultimate-security'),
            selector: row => row.name,
            sortable: true,
        },
        {
            id: 'authenticator',
            name: __('Authenticator', 'ultimate-security'),
            selector: row => row.authenticator,
            sortable: true,
        },
        {
            id: 'active',
            name: __('Active', 'ultimate-security'),
            selector: row => row.is_active === 1 ? __('Active', 'ultimate-security') : __('Inactive', 'ultimate-security'),
            sortable: true,
        },
        {
            id: 'last-used',
            name: __('Last Used', 'ultimate-security'),
            selector: row => row.last_used,
            sortable: true,
        },
        {
            id: 'created-date',
            name: __('Created Date', 'ultimate-security'),
            selector: row => row.created_at,
            sortable: true,
        },
        {
            id: 'actions',
            name: __('Actions', 'ultimate-security'),
            cell: row => (
                <div className="d-flex gap-2">
                    <button
                        className={`button ${row.is_active === 1 ? 'button-secondary' : 'button-primary'}`}
                        onClick={() => handleToggleStatus(row)}
                    >
                        {row.is_active === 1 ? __('Deactivate', 'ultimate-security') : __('Activate', 'ultimate-security')}
                    </button>
                    <button
                        className="button button-danger"
                        onClick={() => handleDelete(row)}
                    >
                        {__('Delete', 'ultimate-security')}
                    </button>
                </div>
            ),
        }
       
    ];

    const filteredData = passkeys?.data?.filter((item) => {
        const matchUser = filters.user_name === '' || item.user_email.toLowerCase().includes(filters.user_name.toLowerCase());
        const matchStatus =
            filters.status === '' ||
            (filters.status === 'active' && item.is_active === 1) ||
            (filters.status === 'inactive' && item.is_active === 0);
        const matchAuthenticator =
            filters.authenticator === '' || item.authenticator === filters.authenticator;
    
        // Fixed date filtering - compare date strings directly
        const createdDateMatch =
            !filters.created_date ||
            item.created_at.split(' ')[0] === filters.created_date; // Extract date part from datetime
    
        const lastUsedDateMatch =
            !filters.last_used_date ||
            item.last_used.split(' ')[0] === filters.last_used_date; // Extract date part from datetime
    
        return matchUser && matchStatus && matchAuthenticator && createdDateMatch && lastUsedDateMatch;
    });
    
    

    return (
        <>
            <div className="setting-unit">
                <div className="user-datatable datatable-unit">
                    <div className="filter-wrapper row g-3 align-items-center mb-4">
                        {/* User Field */}
                        <div className="col-md-2">
                            <label htmlFor="user_name" className="form-label fw-bold">
                                {__("User", "ultimate-security")}
                            </label>
                            <input
                                id="user_name"
                                type="text"
                                name="user_name"
                                className="form-control"
                                value={filters.user_name}
                                onChange={handleInputChange}
                                placeholder="Enter email"
                            />
                        </div>

                        {/* Status Field */}
                        <div className="col-md-2">
                            <label htmlFor="status" className="form-label fw-bold">
                                {__("Status", "ultimate-security")}
                            </label>
                            <select
                                id="status"
                                name="status"
                                className="form-control"
                                value={filters.status}
                                onChange={handleInputChange}
                            >
                                <option value="">{__("Any", "ultimate-security")}</option>
                                <option value="active">{__("Active", "ultimate-security")}</option>
                                <option value="inactive">{__("Inactive", "ultimate-security")}</option>
                            </select>
                        </div>

                        {/* Authenticator Field */}
                        <div className="col-md-2">
                            <label htmlFor="authenticator" className="form-label fw-bold">
                                {__("Authenticator", "ultimate-security")}
                            </label>
                            <select
                                id="authenticator"
                                name="authenticator"
                                className="form-control"
                                value={filters.authenticator}
                                onChange={handleInputChange}
                            >
                                <option value="">Any</option>
                                <option value="Google Password Manager">{__("Google Password Manager", "ultimate-security")}</option>
                                <option value="Windows Hello">{__("Windows Hello", "ultimate-security")}</option>
                            </select>
                        </div>

                        {/* Created Date Field - Fixed to match others */}
                        <div className="col-md-3">
                            <label htmlFor="created_date" className="form-label fw-bold">
                                {__("Created Date", "ultimate-security")}
                            </label>
                            <input
                                id="created_date"
                                type="date"
                                name="created_date"
                                className="form-control"
                                value={filters.created_date}
                                onChange={handleInputChange}
                                placeholder="Select date"
                            />
                        </div>

                        {/* Last Used Date Field - Fixed to match others */}
                        <div className="col-md-3">
                            <label htmlFor="last_used_date" className="form-label fw-bold">
                                {__("Last Used Date", "ultimate-security")}
                            </label>
                            <input
                                id="last_used_date"
                                type="date"
                                name="last_used_date"
                                className="form-control"
                                value={filters.last_used_date}
                                onChange={handleInputChange}
                                placeholder="Select date"
                            />
                        </div>
                    </div>
          
                    <div className="table-wrapper">
                        <DataTable
                            columns={columns}
                            data={filteredData} // Use filtered data
                            pagination
                            paginationPerPage={5} // Default to 5 rows per page
                            paginationRowsPerPageOptions={[5, 10, 15, 25, 50]} // Options with 5 first
                            dense
                            responsive
                        />
                    </div> 
                </div> 
            </div>
        </>
    )
}
export default withForm(Passkeys);